import { getApiClient } from "@src/api";
import { graphql } from "@src/gql";
import {
	type IconType,
	NavBarItemType,
	type NavbarSection,
	WhiteboardPermission,
	WhiteboardStatus,
} from "@src/gql/graphql";
import {
	type QueryClient,
	notifyManager,
	queryOptions,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";

const fetchNavbarBranchQuery = graphql(/* GraphQL */ `
	query fetchNavbarBranch(
		$section: NavbarSection!
		$parentKey: String
		$status: WhiteboardStatus
	) {
		navbar {
			branch(
				section: $section
				parentKey: $parentKey
				where: { status: { eq: $status } }
			) {
				key
				name
				type
				parentKey
				rank
				hasChildren
				status
				icon {
					type
					value
				}
				collaboration {
					isShared
					collaborators {
						userId
						userEmail
						permission
					}
				}
				publication {
					isPublished
				}
			}
		}
	}
`);

// TODO(graphql): same SharedWithMe access field on navbar.tree when available.
const fetchNavbarTreeQuery = graphql(/* GraphQL */ `
	query fetchNavbarTree(
		$section: NavbarSection!
		$status: WhiteboardStatus
	) {
		navbar {
			tree(
				section: $section
				where: { status: { eq: $status } }
			) {
				key
				name
				type
				parentKey
				rank
				hasChildren
				status
				icon {
					type
					value
				}
				collaboration {
					isShared
					collaborators {
						userId
						userEmail
						permission
					}
				}
				publication {
					isPublished
				}
			}
		}
	}
`);

export type NavbarBranchItem = {
	key: string;
	name: string;
	type: NavBarItemType;
	parentKey?: string | null;
	rank: string;
	hasChildren: boolean;
	status?: WhiteboardStatus | null;
	icon?: {
		type: IconType;
		value: string;
	} | null;
	collaboration?: {
		isShared: boolean;
		collaborators?: Array<{
			userId?: string | null;
			userEmail?: string | null;
			permission: WhiteboardPermission;
		}>;
	} | null;
	publication?: { isPublished: boolean } | null;
};

export type SharedWithMeListAccess = Extract<
	WhiteboardPermission,
	WhiteboardPermission.Read | WhiteboardPermission.Write
>;

const SHARED_WITH_ME_LIST_PERMISSIONS = [
	WhiteboardPermission.Read,
	WhiteboardPermission.Write,
] as const satisfies readonly SharedWithMeListAccess[];

function isSharedWithMeListAccess(
	permission: WhiteboardPermission,
): permission is SharedWithMeListAccess {
	return (
		SHARED_WITH_ME_LIST_PERMISSIONS as readonly WhiteboardPermission[]
	).includes(permission);
}

/** Current user's read/write affordance on a Shared With Me board row. */
export function getSharedWithMeListItemAccess(
	item: NavbarBranchItem,
	currentUserId: string,
	currentUserEmail?: string,
): SharedWithMeListAccess | undefined {
	const normalizedEmail = currentUserEmail?.toLowerCase();
	const permission = item.collaboration?.collaborators?.find((c) => {
		if (c.userId === currentUserId) {
			return true;
		}
		// Email invites leave userId null until the invitee has an account id.
		if (c.userId) {
			return false;
		}
		return (
			Boolean(normalizedEmail) && c.userEmail?.toLowerCase() === normalizedEmail
		);
	})?.permission;
	if (permission === undefined || !isSharedWithMeListAccess(permission)) {
		return undefined;
	}
	return permission;
}

export function navbarBranchIsSharedOrPublished(
	item: NavbarBranchItem,
): boolean {
	return (
		(item.collaboration?.isShared ?? false) ||
		(item.publication?.isPublished ?? false)
	);
}

/** Root token for navbar per-branch React Query keys (see `navbarBranchQueryKeys`). */
export const NAVBAR_BRANCH_QUERY_KEY_ROOT = "navbar-branch" as const;

export const navbarBranchQueryKeys = {
	all: (): readonly [typeof NAVBAR_BRANCH_QUERY_KEY_ROOT] => [
		NAVBAR_BRANCH_QUERY_KEY_ROOT,
	],
	section: (section: NavbarSection) =>
		[NAVBAR_BRANCH_QUERY_KEY_ROOT, section] as const,
	branch: (section: NavbarSection, parentKey?: string | null) =>
		[NAVBAR_BRANCH_QUERY_KEY_ROOT, section, parentKey ?? "root"] as const,
};

export const fetchNavbarBranchQueryOptions = (
	section: NavbarSection,
	parentKey?: string | null,
	status: WhiteboardStatus = WhiteboardStatus.Active,
) =>
	queryOptions({
		queryKey: navbarBranchQueryKeys.branch(section, parentKey),
		queryFn: async (): Promise<NavbarBranchItem[]> => {
			const client = getApiClient();
			const response = await client.query({
				query: fetchNavbarBranchQuery,
				variables: { section, parentKey, status },
			});
			return (response.data?.navbar?.branch ?? []) as NavbarBranchItem[];
		},
		networkMode: "offlineFirst",
	});

export const navbarTreeQueryKeys = {
	tree: (section: NavbarSection) => ["navbar-tree", section] as const,
};

export const fetchNavbarTreeQueryOptions = (
	section: NavbarSection,
	status: WhiteboardStatus = WhiteboardStatus.Active,
) =>
	queryOptions({
		queryKey: navbarTreeQueryKeys.tree(section),
		queryFn: async (): Promise<NavbarBranchItem[]> => {
			const client = getApiClient();
			const response = await client.query({
				query: fetchNavbarTreeQuery,
				variables: { section, status },
			});
			return (response.data?.navbar?.tree ?? []) as NavbarBranchItem[];
		},
		networkMode: "offlineFirst",
	});

function parentKeyToBranchCacheKey(
	parentKey: string | null | undefined,
): string {
	if (parentKey == null || parentKey === "") return "root";
	return parentKey;
}

/**
 * Seeds per-branch react-query caches from a flat `navbar.tree` result so
 * `useNavBranchItems` reads synchronously without duplicate network requests.
 */
export function seedBranchCacheFromTree(
	queryClient: QueryClient,
	section: NavbarSection,
	items: NavbarBranchItem[],
): void {
	const buckets = new Map<string, NavbarBranchItem[]>();
	for (const item of items) {
		const bk = parentKeyToBranchCacheKey(item.parentKey);
		const list = buckets.get(bk) ?? [];
		list.push(item);
		buckets.set(bk, list);
	}

	const sortByRank = (a: NavbarBranchItem, b: NavbarBranchItem) =>
		a.rank.localeCompare(b.rank, undefined, { numeric: true });

	notifyManager.batch(() => {
		for (const [bucketKey, branchItems] of buckets) {
			branchItems.sort(sortByRank);
			queryClient.setQueryData<NavbarBranchItem[]>(
				navbarBranchQueryKeys.branch(
					section,
					bucketKey === "root" ? null : bucketKey,
				),
				branchItems,
			);
		}

		const parentKeysWithListedChildren = new Set(buckets.keys());
		for (const item of items) {
			if (item.type !== NavBarItemType.Folder) {
				continue;
			}
			if (!parentKeysWithListedChildren.has(item.key)) {
				queryClient.setQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, item.key),
					[],
				);
			}
		}
	});
}

export function useNavbarTreePrefetch(
	section: NavbarSection,
	options?: { enabled?: boolean },
) {
	const enabled = options?.enabled ?? true;
	const queryClient = useQueryClient();
	const query = useQuery({
		...fetchNavbarTreeQueryOptions(section),
		enabled,
	});
	useEffect(() => {
		if (enabled && query.data) {
			seedBranchCacheFromTree(queryClient, section, query.data);
		}
	}, [enabled, queryClient, section, query.data]);
	return query;
}

/**
 * Returns every navbar branch query key whose cached list contains `itemKey`.
 * Useful for targeted invalidation after mutations that remove/modify a single item.
 */
export function findBranchKeysContaining(
	queryClient: QueryClient,
	itemKey: string,
): readonly (readonly unknown[])[] {
	return queryClient
		.getQueriesData<NavbarBranchItem[]>({
			queryKey: navbarBranchQueryKeys.all(),
		})
		.filter(([, data]) => data?.some((i) => i.key === itemKey))
		.map(([queryKey]) => queryKey);
}

/** Like {@link findBranchKeysContaining} but only keys for the given navbar section. */
export function findBranchKeysContainingInSection(
	queryClient: QueryClient,
	itemKey: string,
	section: NavbarSection,
): readonly (readonly unknown[])[] {
	return findBranchKeysContaining(queryClient, itemKey).filter(
		(key) => key[1] === section,
	);
}

/**
 * Finds an item in any cached navbar branch for the section (root + nested).
 */
export function findNavbarBranchItemInCache(
	queryClient: QueryClient,
	section: NavbarSection,
	key: string,
): NavbarBranchItem | undefined {
	const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.section(section),
	});
	for (const [, data] of queries) {
		if (!data) continue;
		const found = data.find((i) => i.key === key);
		if (found) return found;
	}
	return undefined;
}
